/* Approximate adult sizing; actual garment comparisons take precedence.
 * Inputs stay on the page. No storage, tracking or network requests. */
(() => {
  'use strict';
  const sizes = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  const number = v => v != null && String(v).trim() !== '' && Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null;
  const letter = v => {
    const m = String(v || '').toUpperCase().match(/\b(XXXS|XXS|XS|XXXL|XXL|XL|3XL|2XL|S|M|L)\b/);
    return m ? ({XXXL:'3XL', '2XL':'XXL'}[m[1]] || m[1]) : null;
  };
  const kind = p => {
    const text = `${p.category} ${p.title}`.toLowerCase();
    if (/\b(baby|toddler|kids|children|junior|boys|girls)\b/.test(text)) return 'child';
    if (/\b(trousers|pants|jeans|shorts|joggers|skirts?|leggings|bottoms|trunks)\b/.test(text)) return 'bottom';
    if (/\b(dress(?:es)?|jumpsuits?|rompers?|suits?|tracksuits?|swimwear|underwear|bras?|bikinis?|bodysuits?|lingerie)\b/.test(text)) return 'special';
    return 'top';
  };
  function recommend(p, input) {
    const result = (status, size, detail) => ({status, size, heading: {yes:'Likely to fit', no:'Unlikely to fit', unknown:'Check the measurements', sold:'Currently sold out', invalid:'Check your entries'}[status], detail});
    const variants = p.variants || [];
    if (!variants.some(v => v.available)) return result('sold', null, 'This piece is no longer available.');
    if (kind(p) !== 'top') return result('unknown', null, kind(p) === 'bottom' ? 'Compare waist and inside leg with trousers that fit you.' : 'Use the listing’s measurements to check this piece.');
    const optionIndex = (p.options || []).findIndex(v => /^(size|storlek|taille|größe)$/i.test(v));
    const listed = [...new Set(variants.map(v => v.options?.[optionIndex]).filter(Boolean))];
    const available = [...new Set(variants.filter(v => v.available).map(v => v.options?.[optionIndex]).filter(Boolean))];
    const single = listed.length === 1;
    const fitNote = single ? letter(`${p.title} ${p.description}`.match(/\bfits?\s+(?:like\s+)?(?:an?\s+)?(XXS|XS|XXXL|XXL|XL|3XL|2XL|S|M|L)\b/i)?.[1]) : null;
    const ownPit = number(input.pit), ownLength = number(input.length);
    const pit = number(p.pit) || (number(p.chest) ? number(p.chest) / 2 : null);
    const length = number(p.length);
    if (single && ownPit && pit >= 20 && pit <= 100) {
      if (ownPit < 20 || ownPit > 100 || (ownLength && (ownLength < 20 || ownLength > 180))) return result('invalid', null, 'Enter flat garment measurements in cm.');
      const widthMatches = Math.abs(pit - ownPit) <= 2;
      const lengthMatches = !ownLength || !length || Math.abs(length - ownLength) <= 3;
      const match = widthMatches && lengthMatches;
      return result(match ? 'yes' : 'no', match ? available[0] : null, match ? 'Close to the garment you measured.' : !widthMatches ? `This piece is ${pit < ownPit ? 'narrower' : 'wider'} than your preferred fit.` : `This piece is ${length < ownLength ? 'shorter' : 'longer'} than your preferred fit.`);
    }
    const h = number(input.height), w = number(input.weight);
    if (!h || !w || h < 120 || h > 230 || w < 30 || w > 250) return result('invalid', null, 'Enter your height in cm and weight in kg.');
    if (h < 145 || h > 205 || w < 45 || w > 130) return result('unknown', null, 'Add measurements from a similar garment that fits you.');
    const adjusted = w * (175 / h) ** 2;
    const cutoffs = input.reference === 'women' ? [49, 56, 64, 73, 84, 98, 114] : [53, 61, 70, 80, 92, 106, 122];
    let index = cutoffs.findIndex(c => adjusted < c);
    if (index < 0) index = 7;
    const shift = input.fit === 'relaxed' ? 1 : input.fit === 'close' ? -1 : 0;
    const size = sizes[Math.max(0, Math.min(7, index + shift))];
    const match = available.find(v => (fitNote || letter(v)) === size);
    if (match) return result('yes', match, fitNote ? `Label ${match}; the seller says it fits ${fitNote}.` : 'Based on your height, weight and preferred fit.');
    const comparable = available.map(letter).filter(Boolean);
    if (!comparable.length && !fitNote) return result('unknown', size, 'The listed numeric size needs a brand size chart.');
    return result('no', size, `This piece is available in ${available.join(', ')}${fitNote ? ` (fits ${fitNote})` : ''}.`);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {recommend, kind, letter};
  if (typeof customElements === 'undefined' || customElements.get('cf-size-finder')) return;
  customElements.define('cf-size-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const dialog = this.querySelector('dialog'), open = this.querySelector('.cf-size__open'), form = this.querySelector('form'), output = this.querySelector('.cf-size__result');
      const p = {...this.dataset, options:JSON.parse(this.dataset.options), variants:JSON.parse(this.dataset.variants)};
      // Only present the estimator where it can give a useful recommendation.
      if (kind(p) !== 'top') { this.hidden = true; return; }
      open.hidden = false;
      open.addEventListener('click', () => dialog.showModal());
      this.querySelector('.cf-size__close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', e => { if (e.target === dialog) {const r = dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();} });
      dialog.addEventListener('close', () => open.focus());
      form.addEventListener('input', () => {output.hidden = true;});
      form.addEventListener('submit', e => {
        e.preventDefault();
        const result = recommend(p, Object.fromEntries(new FormData(form)));
        output.replaceChildren(); output.dataset.status = result.status;
        const append = (tag, text, className) => {const el = document.createElement(tag); el.textContent = text; if(className) el.className = className; output.append(el);};
        append('h3', result.heading);
        if (result.size) append('p', `Recommended size: ${result.size}`, 'cf-size__recommendation');
        append('p', result.detail);
        output.hidden = false; output.focus();
      });
    }
  });
})();
